import {Injectable} from '@angular/core';
import {RequestsWebsocketService} from '../../../app-common/services/server/requests-websocket.service';
import {AuthService} from '../../../auth/services/auth/auth.service';
import {AppService} from '../../../app-common/services/app/app.service';
import {Category} from '../../data/category';
import {CheckCategoryEmptyResponse} from '../../data/responses/checkCategoryEmptyResponse';
import {EditCategoryRequest} from '../../data/requests/editCategoryRequest';
import {PleaseWaitService} from '../../../app-common/services/please-wait/please-wait.service';
import {EventEx} from '../../../app-common/data/Event';
import {Query} from '../../../query/data/query';
import {QueryCreator, QuerySort} from '../../../query/data/queryCreator';
import {CategoryProperties} from '../../../query/data/specifics/categoryProperties';
import {QueryableProperty} from '../../../query/data/queryableProperty';
import {ContainsValuePropertyFilter} from '../../../query/data/containsValuePropertyFilter';
import {CategoryDto} from '../../data/categoryDto';
import {ReflectionUtils} from '../../../virtual-scroll/services/reflection-utils';
import {CategoryUtils} from '../../data/categoryUtils';
import {ApiService} from '../../../app-common/services/server/api.service';
import {ConflictException} from '../../../app-common/services/server/conflictException';

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  onConflict: EventEx = new EventEx();

  constructor(public authService: AuthService,
              public appService: AppService,
              public httpService: ApiService,
              public pleaseWaitService: PleaseWaitService,
              public websocket: RequestsWebsocketService) {
    this.authService = authService;
    this.appService = appService;
    this.websocket = websocket;
  }

  copyAttributes(target: Category, original: Category) {
    ReflectionUtils.copyAttributes(original, target);
  }

  copyDtoAttributes(target: CategoryDto, original: CategoryDto) {
    const newCat = new Category();
    ReflectionUtils.copyAttributes(original, target);
    target.category = newCat;
  }

  cloneDto(original: CategoryDto) {
    const categoryDto = new CategoryDto();
    this.copyDtoAttributes(categoryDto, original);
    return categoryDto;
  }

  async getCategory(id: number, force: boolean = false) {
    const response = await this.httpService.applicationApiClient.getWithParams<CategoryDto>(`Category/${id}`, {force});
    return response;
  }

  async getAllCategories() {
    const query = this.buildSearchQueryModel('', 999, 0);
    const response = await this.httpService.applicationApiClient.getWithParams<CategoryDto[]>(`Category/Search`, {filter: JSON.stringify(query)});
    return response;
  }


  async addCategory(category: Category) {
    this.pleaseWaitService.addToQueue();
    let response;
    try {
      response = await this.httpService.applicationApiClient.post('Category', category);
      this.appService.showSuccessMessage('Category Added');
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async editCategory(category: Category, force: boolean = false) {
    const request = new EditCategoryRequest();
    this.pleaseWaitService.addToQueue();
    request.category = category;
    request.force = force;
    let response;
    try {
      response = await this.httpService.applicationApiClient.put('Category', category.id, request);
      this.appService.showSuccessMessage('Category Edited');
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      if (ex instanceof ConflictException) {
        this.onConflict.emit({my: category, db: ex.conflict});
      }
      return false;
    }
  }

  async deleteCategory(categoryId: number) {
    this.pleaseWaitService.addToQueue();
    try {
      const response = await this.httpService.applicationApiClient.delete(`Category`, categoryId);
      this.appService.showSuccessMessage('Attachment Deleted');
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  getAllCategory() {
    const category = new Category();
    CategoryUtils.setDefaultProperties(category);
    category.name = 'All';
    return category;
  }

  getAllDto() {
    const dto = new CategoryDto();
    dto.category = this.getAllCategory();
    return dto;
  }

  async checkCategoryEmpty(categoryId: number) {
    const response = await this.httpService.applicationApiClient.get<CheckCategoryEmptyResponse>(`Category/${categoryId}/IsEmpty`);
    return response;
  }

  buildSearchQueryModel(query: string, entriesPerPage: number, pageNumber: number) {
    const customerProperties = new CategoryProperties();
    const rootQuery = new Query();

    for (const property of customerProperties.properties) {
      if (property.name === 'name') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'shortDescription') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
    }
    const sorting = new QuerySort();
    sorting.dir = customerProperties.sortingDescending ? 'desc' : 'asc';
    sorting.field = customerProperties.sortingPropertyName;

    return QueryCreator.generateFilter(rootQuery, sorting, entriesPerPage, pageNumber, 'or');
  }

  private createContainsFilter(property: QueryableProperty, toSearch: any) {
    const result = new ContainsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = toSearch;
    return result;
  }
}

